if (!customElements.get("offcanvas-toggle-button")) {
  // Shared across all card instances so the size panel's scroll position is
  // restored correctly regardless of which instance handles the close.
  let offcanvasScrollState = null;

  customElements.define(
    "offcanvas-toggle-button",
    class OffcanvasCardProduct extends HTMLElement {
      constructor() {
        super();

        if (
          !window.matchMedia("(max-width: 1023px)").matches &&
          this.closest("#product-grid")
        ) {
          return;
        }

        this.options = {
          isOpen: "is-open",
          offcanvasContent: ".offcanvas-content-wrapper",
          offcanvasProductCloned: "offcanvas-card-product-cloned",
          offcanvasClose: ".offcanvas-close",
        };

        this.body = document.body;

        this.registerEvents();
      }

      registerEvents() {
        this.init();

        // Update loadedItems on product-grid-updated, but DO NOT re-call init().
        // Re-calling init() stacked another click listener on every existing
        // card every time a new page was appended — after a few infinite-scroll
        // pages, each button had 5-10+ duplicate listeners all firing on click.
        document.addEventListener("product-grid-updated", (event) => {
          this.loadedItems = event.detail;
        });

        // NOTE: the size picker is closed by onToggleButtonClick (on variant
        // tap) and by its close button. It is intentionally NOT closed on
        // "product-collection-added": that event fires when an add request
        // resolves, by which point the shopper may have opened a different
        // card's picker — closing "the open one" would shut the wrong panel.
      }

      init() {
        // Guard: each instance should bind its click listener exactly once.
        if (this._clickBound) return;
        this._clickBound = true;
        this.offcanvasToggleButton = this;
        this.openButtonClick(this.offcanvasToggleButton);
      }

      openButtonClick(button) {
        const card = button?.closest(".card"),
          offcanvasCardWrapper = card?.querySelector("offcanvas-item");

        if (!!offcanvasCardWrapper) {
          button.addEventListener("click", () => {
            this.onToggleButtonClick(offcanvasCardWrapper);
          });
        } else {
          button.addEventListener("click", () => {
            const cardTargetElement = button.closest(".card");

            let timerToShow = 0,
              targetOffcanvasWrapper =
                cardTargetElement.querySelector("offcanvas-item");

            if (!targetOffcanvasWrapper) {
              const targetElement = cardTargetElement.querySelector(
                  ".product-image-mobile",
                ),
                id = button.getAttribute("data-target-id"),
                item = this.loadedItems.find((item) => item.id === id);

              targetElement.insertAdjacentElement("afterend", item.element);
              document.dispatchEvent(new CustomEvent("card-product-loaded"));

              timerToShow = 25;
            }

            targetOffcanvasWrapper = targetOffcanvasWrapper
              ? targetOffcanvasWrapper
              : cardTargetElement.querySelector("offcanvas-item");

            setTimeout(() => {
              this.onToggleButtonClick(targetOffcanvasWrapper);
            }, timerToShow);
          });
        }
      }

      onToggleButtonClick(offcanvasCardItemWrapper) {
        const content = offcanvasCardItemWrapper?.querySelector(
          this.options.offcanvasContent,
        );
        const variants = content.getAttribute("data-variants-count");

        if (variants > 1) {
          this.togglePanel(offcanvasCardItemWrapper, true);

          const clonedPanel = document.querySelector(
            `.${this.options.offcanvasProductCloned}`,
          );
          const closePanel = (scrollToTop = false) =>
            this.togglePanel(offcanvasCardItemWrapper, false, scrollToTop);

          const closePanelAfterAdd = () => closePanel(true);

          clonedPanel
            ?.querySelectorAll(this.options.offcanvasClose)
            .forEach((item) =>
              item.addEventListener("click", closePanel, { once: true }),
            );

          // Picking a size closes the panel straight away, so the add-to-cart
          // popup is not stacked on top of it and the scroll position is
          // restored by the instance that opened the panel.
          // closePanelAfterAdd signals the drawer to scroll to top on close.
          clonedPanel
            ?.querySelectorAll(".product-form__variant-button")
            .forEach((item) =>
              item.addEventListener("click", closePanelAfterAdd, { once: true }),
            );
        } else {
          this.querySelector(".loading__spinner")?.classList.remove("hidden");

          content.querySelector("[data-variant-id]").click();
        }
      }

      togglePanel(targetElement, state, scrollToTop = false) {
        if (!targetElement) return;

        if (state) {
          // Prevent opening a second panel if one is already present.
          if (document.querySelector(`.${this.options.offcanvasProductCloned}`))
            return;

          const clonedTarget = targetElement.cloneNode(true);
          clonedTarget.classList.add(this.options.isOpen);
          clonedTarget.classList.add(this.options.offcanvasProductCloned);

          // When the ATC drawer is open on desktop, append the size picker
          // clone inside it instead of the body. The drawer uses
          // "contain: layout" which makes it the containing block for
          // position:fixed descendants, scoping the picker to the panel.
          // In this context, no page scroll lock is needed.
          const atcDrawer = document.querySelector(
            ".added-to-cart-drawer.is-active .atc-drawer"
          );

          if (atcDrawer) {
            atcDrawer.appendChild(clonedTarget);
            // Mark the clone so the close handler always knows the context,
            // even if the drawer re-renders and disconnects the clone node.
            clonedTarget.dataset.insideDrawer = "true";
            // Activate the overlay that dims the page behind the picker
            // when it is scoped inside the ATC drawer.
            atcDrawer.closest(".added-to-cart-drawer")
              ?.classList.add("picker-is-open");
          } else {
            // Default behaviour outside the ATC drawer: lock page scroll
            // and append to the body as usual.
            offcanvasScrollState = window.scrollY;
            document.body.style.overflow = "hidden";
            document.body.style.position = "fixed";
            document.body.style.top = `-${offcanvasScrollState}px`;
            document.body.style.width = "100%";
            document.body.appendChild(clonedTarget);
          }

          document.dispatchEvent(new CustomEvent("card-product-loaded"));

          // Warm the image cache while the shopper picks a size, so the
          // add-to-cart popup can show its photo instantly on selection.
          const preloadUrl = clonedTarget.querySelector("[data-product-image]")
            ?.dataset.productImage;
          if (preloadUrl) new Image().src = preloadUrl;
        } else {
          // Use document.querySelector instead of document.body.querySelector
          // so the clone is found whether it lives in the body or the ATC drawer.
          const cloned = document.querySelector(
            `.${this.options.offcanvasProductCloned}`
          );
          if (!cloned) return;

          // Check whether the clone was scoped inside the ATC drawer so we
          // know whether page scroll needs to be restored on close.
          // Read from dataset instead of DOM traversal — renderContents()
          // may have already replaced the drawer HTML and disconnected the
          // clone by the time this runs, making closest() unreliable.
          const isInsideDrawer = cloned.dataset.insideDrawer === "true";

          // Remove the overlay class as soon as we start closing.
          if (isInsideDrawer) {
            document
              .querySelector(".added-to-cart-drawer")
              ?.classList.remove("picker-is-open");
          }

          const restoreScroll = () => {
            // Nothing to restore when the picker was scoped inside the drawer —
            // the page scroll lock was never applied in that case.
            if (isInsideDrawer) return;
            document.body.style.overflow = "";
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.width = "";
            document.querySelector("html").style.scrollBehavior = "auto";
            window.scrollTo(0, offcanvasScrollState || 0);
            offcanvasScrollState = null;
            document.querySelector("html").style.scrollBehavior = "";
          };

          const finalize = () => {
            if (!cloned.isConnected) return;
            // Removing the node drops `.is-open`, releasing the body
            // scroll-lock; restore the scroll position in the same frame so
            // the page doesn't jump.
            cloned.remove();
            restoreScroll();

            // Scroll to top of the ATC drawer only when closing after a
            // successful add-to-cart (scrollToTop = true). Manual close (X
            // button) keeps the drawer at its current scroll position.
            if (isInsideDrawer && scrollToTop) {
              const drawer = document.querySelector(".added-to-cart-drawer");
              if (drawer) {
                const scrollTarget = () => {
                  const body = drawer.querySelector(".atc-drawer__body");
                  if (!body || body.scrollTop === 0) return;

                  const start = body.scrollTop;
                  const duration = Math.min(400, start * 0.6); // faster for short distances
                  let startTime = null;

                  const ease = (t) => 1 - Math.pow(1 - t, 3); // ease-out cubic

                  const step = (timestamp) => {
                    if (!startTime) startTime = timestamp;
                    const elapsed = timestamp - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    body.scrollTop = start * (1 - ease(progress));
                    if (progress < 1) requestAnimationFrame(step);
                  };

                  requestAnimationFrame(step);
                };
                // Double rAF ensures the browser has painted the post-removal
                // state before resetting the scroll position.
                requestAnimationFrame(() => requestAnimationFrame(scrollTarget));
              }
            }
          };

          // On mobile the picker is a bottom sheet: slide it down first, then
          // remove. The `.atc-offcanvas-closing` class overrides the open
          // transform to translateY(100%) while keeping `.is-open`, so the
          // scroll-lock holds until the slide finishes (no mid-animation jump).
          const wrapper = cloned.querySelector(this.options.offcanvasContent);
          if (wrapper && window.matchMedia("(max-width: 1023px)").matches) {
            cloned.classList.add("atc-offcanvas-closing");

            let done = false;
            const onEnd = (event) => {
              if (done) return;
              if (
                event &&
                event.propertyName &&
                event.propertyName !== "transform"
              )
                return;
              done = true;
              wrapper.removeEventListener("transitionend", onEnd);
              finalize();
            };
            wrapper.addEventListener("transitionend", onEnd);
            setTimeout(onEnd, 450); // fallback if transitionend doesn't fire
          } else {
            finalize();
          }
        }
      }
    },
  );
}
